<script lang="ts">
  import { onMount } from 'svelte';
  import type { GameState } from '@rsvjelle/game-core';
  import type { AccountSummary } from '../lib/account-status.ts';
  import {
    CloudApiError, acquireLease, changePassword, currentUser, deleteCloudAccount,
    getCloudSave, login, logout, putCloudSave, register, requestPasswordRecovery,
    resetPassword, updateRecoveryEmail, type CloudSave, type CloudUser
  } from '../lib/cloud-client.ts';

  interface Props {
    gameState: GameState;
    onReplace: (state: GameState) => Promise<void>;
    onLink: (userId: string) => Promise<void>;
    snapshot: () => GameState;
    onStatusChange: (status: AccountSummary) => void;
  }
  let { gameState, onReplace, onLink, snapshot, onStatusChange }: Props = $props();
  let mode = $state<'login' | 'register'>('login');
  let username = $state('');
  let email = $state('');
  let password = $state('');
  let user = $state<CloudUser | null>(null);
  let cloud = $state<CloudSave | null>(null);
  let busy = $state(false);
  let message = $state('Local progress is protected on this device.');
  let needsTakeover = $state(false);
  let synced = $state(false);
  let cloudRevision = $state(0);
  let syncTimer: ReturnType<typeof setInterval> | null = null;
  let accountAction = $state<'email' | 'password' | 'delete' | null>(null);
  let currentPassword = $state('');
  let newPassword = $state('');
  let confirmPassword = $state('');
  let deletePassword = $state('');
  let deleteConfirmation = $state('');
  let emailPassword = $state('');
  let recoveryMode = $state(false);
  let recoverySent = $state(false);
  let resetToken = $state(new URLSearchParams(window.location.search).get('reset') ?? '');
  let resetPasswordValue = $state('');
  let resetPasswordConfirmation = $state('');
  let accountChecked = $state(false);
  let reconnecting = $state(false);
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  let lastStatusKey = '';

  $effect(() => {
    const status: AccountSummary = !accountChecked || reconnecting
      ? { status: 'checking' }
      : synced && user
        ? { status: 'synced', username: user.username }
        : user
          ? { status: 'attention', username: user.username }
          : { status: 'local' };
    const key = `${status.status}:${status.username ?? ''}`;
    if (key !== lastStatusKey) {
      lastStatusKey = key;
      onStatusChange(status);
    }
  });

  const revisionKey = (id: string) => `rsv2.cloud-revision.${id}`;

  function explain(error: unknown): string {
    if (error instanceof CloudApiError) {
      if (error.code === 'device_active') return 'This account is currently active on another device.';
      if (error.code === 'revision_conflict') return 'A newer cloud save exists. Choose which progress to keep.';
      if (error.code === 'invalid_credentials') return 'Username, email, or password is incorrect.';
      return error.message;
    }
    return 'The cloud service is temporarily unavailable.';
  }

  async function establish(force = false): Promise<void> {
    if (!user) return;
    try {
      await acquireLease(force);
      needsTakeover = false;
      cloud = await getCloudSave();
      const knownRevision = Number(localStorage.getItem(revisionKey(user.id)) ?? -1);
      if (!cloud.state) {
        await onLink(user.id);
        const result = await putCloudSave(snapshot(), 0);
        activate(result.revision);
      } else if (gameState.playerId === user.id && knownRevision >= 0) {
        cloudRevision = knownRevision;
        await syncNow();
      } else {
        synced = false;
        message = 'Cloud and device progress found. Choose once which save should lead.';
      }
    } catch (error) {
      needsTakeover = error instanceof CloudApiError && (error.code === 'device_active' || error.code === 'lease_required');
      message = explain(error);
    }
  }

  function activate(revision: number): void {
    if (!user) return;
    cloudRevision = revision;
    localStorage.setItem(revisionKey(user.id), String(revision));
    synced = true;
    cloud = null;
    message = 'Cloud sync active · saves every 20 seconds';
    if (syncTimer) clearInterval(syncTimer);
    syncTimer = setInterval(() => void syncNow(), 20_000);
  }

  async function syncNow(): Promise<void> {
    if (!user || busy) return;
    busy = true;
    try {
      await acquireLease(false);
      const result = await putCloudSave(snapshot(), cloudRevision);
      activate(result.revision);
    } catch (error) {
      synced = false;
      if (syncTimer) clearInterval(syncTimer);
      syncTimer = null;
      if (error instanceof CloudApiError && error.code === 'revision_conflict') cloud = await getCloudSave().catch(() => null);
      needsTakeover = error instanceof CloudApiError && error.code === 'lease_required';
      message = explain(error);
    } finally { busy = false; }
  }

  function scheduleReconnect(attempt = 0): void {
    if (!user || document.visibilityState !== 'visible') return;
    if (reconnectTimer) clearTimeout(reconnectTimer);
    reconnecting = true;
    message = attempt === 0 ? 'Restoring cloud connection…' : 'Cloud is waking up… retrying automatically.';
    const delays = [700, 2_000, 5_000];
    reconnectTimer = setTimeout(() => {
      reconnectTimer = null;
      void (async () => {
        if (!user || document.visibilityState !== 'visible') { reconnecting = false; return; }
        if (synced) await syncNow(); else await establish(false);
        if (synced || needsTakeover || cloud?.state || attempt >= delays.length - 1) {
          reconnecting = false;
          return;
        }
        scheduleReconnect(attempt + 1);
      })();
    }, delays[Math.min(attempt, delays.length - 1)]);
  }

  async function submit(): Promise<void> {
    busy = true; message = '';
    try {
      if (mode === 'register') await register(username, email, password); else await login(username, password);
      user = await currentUser();
      password = '';
      await establish(false);
    } catch (error) { message = explain(error); }
    finally { busy = false; }
  }

  async function sendRecovery(): Promise<void> {
    busy = true;
    try {
      await requestPasswordRecovery(username);
      recoverySent = true;
      message = 'If a matching account has a recovery email, a reset link is on its way. The email also includes your username.';
    } catch (error) { message = explain(error); }
    finally { busy = false; }
  }

  async function finishPasswordReset(): Promise<void> {
    if (resetPasswordValue !== resetPasswordConfirmation) { message = 'Password confirmation does not match.'; return; }
    busy = true;
    try {
      await resetPassword(resetToken, resetPasswordValue);
      try { await logout(); } catch { /* Reset already invalidated every server session. */ }
      if (syncTimer) clearInterval(syncTimer);
      syncTimer = null; user = null; cloud = null; synced = false;
      resetToken = ''; resetPasswordValue = ''; resetPasswordConfirmation = '';
      const url = new URL(window.location.href);
      url.searchParams.delete('reset');
      history.replaceState(null, '', `${url.pathname}${url.search}${url.hash}`);
      message = 'Password reset. Sign in with your new password; all older sessions were signed out.';
    } catch (error) { message = explain(error); }
    finally { busy = false; }
  }

  async function saveRecoveryEmail(): Promise<void> {
    busy = true;
    try {
      const result = await updateRecoveryEmail(email, emailPassword);
      if (user) user = { ...user, email: result.email };
      emailPassword = ''; accountAction = null;
      message = 'Recovery email saved.';
    } catch (error) { message = explain(error); }
    finally { busy = false; }
  }

  async function useCloud(): Promise<void> {
    if (!cloud?.state || !user) return;
    busy = true;
    try {
      await onReplace(cloud.state);
      activate(cloud.revision);
    } finally { busy = false; }
  }

  async function keepDevice(): Promise<void> {
    if (!cloud || !user) return;
    busy = true;
    try {
      await onLink(user.id);
      const result = await putCloudSave(snapshot(), cloud.revision);
      activate(result.revision);
    } catch (error) { message = explain(error); }
    finally { busy = false; }
  }

  async function signOut(): Promise<void> {
    busy = true;
    try { await logout(); } catch { /* Local sign-out still stops synchronization. */ }
    if (syncTimer) clearInterval(syncTimer);
    syncTimer = null; user = null; cloud = null; synced = false; busy = false;
    message = 'Signed out. Local saves continue normally.';
  }

  async function updatePassword(): Promise<void> {
    if (newPassword !== confirmPassword) { message = 'New password confirmation does not match.'; return; }
    busy = true;
    try {
      await changePassword(currentPassword, newPassword);
      currentPassword = ''; newPassword = ''; confirmPassword = ''; accountAction = null;
      message = 'Password changed. Other signed-in devices have been signed out.';
    } catch (error) { message = explain(error); }
    finally { busy = false; }
  }

  async function removeAccount(): Promise<void> {
    if (!user || deleteConfirmation !== user.username) { message = `Type ${user?.username ?? 'your username'} exactly to confirm.`; return; }
    busy = true;
    try {
      await deleteCloudAccount(deletePassword);
      if (syncTimer) clearInterval(syncTimer);
      syncTimer = null;
      user = null; cloud = null; synced = false; accountAction = null; deletePassword = ''; deleteConfirmation = '';
      message = 'Account and cloud save deleted. This device keeps its local progress.';
    } catch (error) { message = explain(error); }
    finally { busy = false; }
  }

  onMount(() => {
    const handleVisibility = () => { if (document.visibilityState === 'visible') scheduleReconnect(); };
    const handleOnline = () => scheduleReconnect();
    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('online', handleOnline);
    void currentUser()
      .then(async (found) => { user = found; email = found.email ?? ''; await establish(false); })
      .catch(() => { message = 'Cloud status is unavailable. Local saves continue normally.'; })
      .finally(() => { accountChecked = true; });
    return () => {
      if (syncTimer) clearInterval(syncTimer);
      if (reconnectTimer) clearTimeout(reconnectTimer);
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('online', handleOnline);
    };
  });
</script>

<article class="account-panel" id="account-panel">
  <div class="account-heading"><div><p class="eyebrow">REALM ACCOUNT</p><h3>{user ? user.username : 'Protect your progress'}</h3></div><span class:online={synced}>{synced ? '● Synced' : user ? '● Attention' : '○ Local only'}</span></div>
  <p class="account-message">{message}</p>
  {#if resetToken}
    <form onsubmit={(event) => { event.preventDefault(); void finishPasswordReset(); }}>
      <label>New password<input bind:value={resetPasswordValue} type="password" autocomplete="new-password" minlength="10" maxlength="128" required /></label>
      <label>Repeat password<input bind:value={resetPasswordConfirmation} type="password" autocomplete="new-password" minlength="10" maxlength="128" required /></label>
      <button type="submit" disabled={busy}>{busy ? 'Resetting…' : 'Set new password'}</button>
    </form>
  {:else if !user && recoveryMode}
    <form onsubmit={(event) => { event.preventDefault(); void sendRecovery(); }}>
      <label>Username or email<input bind:value={username} autocomplete="username" minlength="3" maxlength="254" required /></label>
      <button type="submit" disabled={busy || recoverySent}>{busy ? 'Sending…' : recoverySent ? 'Email requested' : 'Send reset link'}</button>
    </form>
    <button class="text-button" type="button" onclick={() => { recoveryMode = false; recoverySent = false; message = ''; }}>Back to sign in</button>
  {:else if !user}
    <form onsubmit={(event) => { event.preventDefault(); void submit(); }}>
      <label>{mode === 'register' ? 'Username' : 'Username or email'}<input bind:value={username} autocomplete="username" minlength="3" maxlength={mode === 'register' ? 24 : 254} required /></label>
      {#if mode === 'register'}<label>Email<input bind:value={email} type="email" autocomplete="email" maxlength="254" required /></label>{/if}
      <label>Password<input bind:value={password} type="password" autocomplete={mode === 'register' ? 'new-password' : 'current-password'} minlength="10" maxlength="128" required /></label>
      <button type="submit" disabled={busy}>{busy ? 'Connecting…' : mode === 'register' ? 'Create account' : 'Sign in'}</button>
    </form>
    <button class="text-button" type="button" onclick={() => { mode = mode === 'login' ? 'register' : 'login'; message = ''; }}>
      {mode === 'login' ? 'New player? Create an account' : 'Already registered? Sign in'}
    </button>
    {#if mode === 'login'}<button class="text-button" type="button" onclick={() => { recoveryMode = true; recoverySent = false; message = ''; }}>Forgot password?</button>{/if}
  {:else if needsTakeover}
    <div class="account-actions"><button type="button" onclick={() => void establish(true)} disabled={busy}>Take over on this device</button><button class="secondary" type="button" onclick={() => void signOut()}>Cancel</button></div>
    <small>Taking over stops the other device from writing. Its unsynced local progress remains recoverable there.</small>
  {:else if cloud?.state}
    <div class="save-choice"><button type="button" onclick={() => void useCloud()} disabled={busy}><strong>Use cloud save</strong><small>Last synced {cloud.updatedAt ? new Date(cloud.updatedAt).toLocaleString() : 'earlier'}</small></button><button type="button" onclick={() => void keepDevice()} disabled={busy}><strong>Keep this device</strong><small>Explicitly replace cloud progress</small></button></div>
  {:else}
    <div class="account-actions"><button type="button" onclick={() => { if (synced) void syncNow(); else scheduleReconnect(); }} disabled={busy || reconnecting}>{busy || reconnecting ? 'Connecting…' : synced ? 'Sync now' : 'Reconnect cloud'}</button><button class="secondary" type="button" onclick={() => void signOut()} disabled={busy}>Sign out</button></div>
  {/if}
  {#if user && !needsTakeover && !cloud?.state}
    <div class="account-management">
      <button class="text-button" type="button" onclick={() => { accountAction = accountAction === 'email' ? null : 'email'; email = user?.email ?? ''; }}>{user.email ? 'Change recovery email' : 'Add recovery email'}</button>
      <button class="text-button" type="button" onclick={() => { accountAction = accountAction === 'password' ? null : 'password'; }}>Change password</button>
      <button class="text-button danger-text" type="button" onclick={() => { accountAction = accountAction === 'delete' ? null : 'delete'; }}>Delete account</button>
    </div>
    {#if accountAction === 'email'}
      <form class="account-subform" onsubmit={(event) => { event.preventDefault(); void saveRecoveryEmail(); }}>
        <label>Recovery email<input bind:value={email} type="email" autocomplete="email" maxlength="254" required /></label>
        <label>Current password<input bind:value={emailPassword} type="password" autocomplete="current-password" required /></label>
        <button type="submit" disabled={busy}>Save email</button>
      </form>
    {:else if accountAction === 'password'}
      <form class="account-subform" onsubmit={(event) => { event.preventDefault(); void updatePassword(); }}>
        <label>Current password<input bind:value={currentPassword} type="password" autocomplete="current-password" required /></label>
        <label>New password<input bind:value={newPassword} type="password" autocomplete="new-password" minlength="10" maxlength="128" required /></label>
        <label>Repeat new password<input bind:value={confirmPassword} type="password" autocomplete="new-password" minlength="10" maxlength="128" required /></label>
        <button type="submit" disabled={busy}>Update password</button>
      </form>
    {:else if accountAction === 'delete'}
      <form class="account-subform danger-zone" onsubmit={(event) => { event.preventDefault(); void removeAccount(); }}>
        <p><strong>This permanently deletes the account and cloud save.</strong> Local progress on this device remains available.</p>
        <label>Password<input bind:value={deletePassword} type="password" autocomplete="current-password" required /></label>
        <label>Type {user.username}<input bind:value={deleteConfirmation} autocomplete="off" required /></label>
        <button type="submit" disabled={busy || deleteConfirmation !== user.username}>Delete permanently</button>
      </form>
    {/if}
  {/if}
</article>
